import { gql, useMutation } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import Button from '../../components/Button';
import { Field, FieldSet } from '../../components/Form';
import Tooltip from '../../components/Tooltip';
import type { ScimTab_IntegrationFragment } from '../../graphql/generated/graphqlTypes';
import copyIcon from '../../icons/copy.svg';
import type { IntegrationPlatformInfo } from '../../integrations/IntegrationPlatformInfo';
import UIActions from '../../ui/UIActions';
import styles from './SCIMTab.module.scss';

const REGENERATE_SCIM_TOKEN_MUTATION = gql`
  mutation regenerateScimToken($platform: String!) {
    regenerateScimToken(platform: $platform) {
      id
      settings {
        scimToken
      }
    }
  }
`;

const INTEGRATION_FRAGMENT = gql`
  fragment SCIMTab_integration on Integration {
    id
    platform
    settings {
      enableScim
      scimToken
    }
  }
`;

interface SCIMTabProps {
  integration: ScimTab_IntegrationFragment;
  platformInfo: IntegrationPlatformInfo;
}

const SCIMTab = ({
  integration: { platform, settings },
  platformInfo,
}: SCIMTabProps) => {
  const dispatch = useDispatch();
  const [regenerateScimToken] = useMutation(REGENERATE_SCIM_TOKEN_MUTATION, {
    variables: { platform },
  });

  const onRegenerateScimToken = () => {
    const tabUrl = document.location.toString();

    dispatch(
      UIActions.confirmModal({
        title: 'Generate a new SCIM Token?',
        message: (
          <p>
            This will cause the old token to stop working immediately, and the
            new token must be added in your {platformInfo.name} SCIM settings
            before SCIM starts working again.
          </p>
        ),
        confirmButtonText: 'Regenerate Token',
        onConfirm: () => {
          regenerateScimToken().then(() => {
            document.location = tabUrl;
          });
        },
      }),
    );
  };

  // eslint-disable-next-line react/no-unstable-nested-components -- Fix next time editing the file
  const CopyToken = () => {
    const [hasCopied, setCopied] = useState(false);
    const tooltipText = hasCopied ? 'Copied!' : 'Copy token';

    useEffect(() => {
      if (hasCopied) {
        const timeout = setTimeout(() => setCopied(false), 2000);
        return () => clearTimeout(timeout);
      }

      return undefined;
    }, [hasCopied]);

    const onClick = async () => {
      if (!settings?.scimToken) {
        return;
      }
      await navigator.clipboard.writeText(settings.scimToken);
      setCopied(true);
    };

    return (
      <Tooltip tooltip={tooltipText} placement="top" trigger="hover">
        <Button icon={copyIcon} onClick={onClick} />
      </Tooltip>
    );
  };

  return (
    <FieldSet>
      <Field
        name="enableScim"
        type="checkbox"
        label="Enable SCIM Provisioning"
      />

      <Field
        name="scimToken"
        label="SCIM API Token"
        placeholder="Enable SCIM to generate token"
        append={settings?.scimToken && <CopyToken />}
        className={styles.scimToken}
        disabled
      />

      {settings?.scimToken && (
        <Button
          variant="link"
          className={styles.regenerateToken}
          onClick={onRegenerateScimToken}
        >
          Regenerate Token
        </Button>
      )}
    </FieldSet>
  );
};

SCIMTab.fragments = {
  integration: INTEGRATION_FRAGMENT,
};

SCIMTab.getFormValues = ({ settings }: ScimTab_IntegrationFragment) => ({
  enableScim: settings?.enableScim || false,
  scimToken: settings?.scimToken,
});

SCIMTab.getNewSettings = (
  values: ReturnType<typeof SCIMTab.getFormValues>,
) => ({ enableScim: values.enableScim });

export default SCIMTab;
